import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { supabase, createPatientRealtimeClient } from './SupabaseClient';
import { jwtDecode } from 'jwt-decode'; // Ensure you have this installed

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const patientClientRef = useRef(null);
  const initializeAuth = async () => {
    try {
      // First, check if there is an active native Supabase Admin Session
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        setUser({
          ...session.user,
          role: 'admin', // Label this user as admin
          token: session.access_token
        });
        setLoading(false);
        return;
      }

      // If no Admin session, look for a Patient Token in sessionStorage
      const patientToken = sessionStorage.getItem('access_token');
      // this is for the realtime query 
      let client;
      if (patientToken) {

        patientClientRef.current = client; // Store the client in a ref for later use
        const decoded = jwtDecode(patientToken);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
          sessionStorage.removeItem('access_token');
          return;
        }
        client = await createPatientRealtimeClient(patientToken);
        setUser({
          id: decoded.user_metadata.patient_id,
          patientId: decoded.user_metadata.patient_id,
          role: 'patient', // Label this user as patient
          token: patientToken,
          client: client
        });

      }
    } catch (err) {
      console.error("Auth state hydration failed:", err);
    } finally {
      setLoading(false); // Always turn off loading state
    }
  };


  useEffect(() => {
    // 1. Core initialization function

    initializeAuth();

    // 2. Listen for native Supabase Admin login/logout events
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({
          ...session.user,
          role: 'admin',
          token: session.access_token
        });
      } else {
        // Only wipe user state if the current user session is an admin
        // This stops Supabase from wiping out custom patient data logs
        setUser((currentUser) => (currentUser?.role === 'admin' ? null : currentUser));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = (email, password) =>
    supabase.auth.signInWithPassword({ email, password });

  const logout = async () => {
    // Clear everything depending on who is logging out
    sessionStorage.removeItem('access_token');
    await supabase.auth.signOut();
    setUser(null);
  };

  // Add this inside AuthProvider
  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      if (error.message.includes('AAL2')) {
        throw new Error('AAL2_REQUIRED');
      }
      throw error;
    }
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, login, initializeAuth, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}